"use client";

import { useLayoutEffect, useState } from "react";
import { useIsClient } from "./useIsClient";

export const MD_MEDIA_QUERY = "(min-width: 768px)";
export const LG_MEDIA_QUERY = "(min-width: 1024px)";

type UseMediaQueryOptions = {
  defaultValue?: boolean;
  initializeWithValue?: boolean;
};

const IS_SERVER = typeof window === "undefined";

export function useMediaQuery(
  query: string,
  { defaultValue = false, initializeWithValue = true }: UseMediaQueryOptions = {},
): boolean {
  const getMatches = (query: string): boolean => {
    if (IS_SERVER) return defaultValue;
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean>(() => {
    if (initializeWithValue) return getMatches(query);
    return defaultValue;
  });

  // Handles the change event of the media query.
  function handleChange() {
    setMatches(getMatches(query));
  }

  useLayoutEffect(() => {
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    handleChange();

    // Use deprecated `addListener` and `removeListener` to support Safari < 14 (#135)
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener("change", handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener("change", handleChange);
      }
    };
  }, [query]);

  return matches;
}

/**
 * Hydration-safe variant: `isMounted` stays false until the viewport has been measured on the client,
 * so callers can render neither the desktop nor the mobile branch until the width is known.
 */
export function useClientMediaQuery(query: string): { matches: boolean; isMounted: boolean } {
  const isMounted = useIsClient();
  const matches = useMediaQuery(query, { initializeWithValue: false });
  return { matches: isMounted && matches, isMounted };
}
